import React, { useState, useEffect } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import useApi from '../hooks/useApi';
// Recharts imports removed
import { FiDownload, FiUsers, FiClock } from 'react-icons/fi';
import GlassCard from '../components/ui/GlassCard';
import GradientButton from '../components/ui/GradientButton';
import GradientText from '../components/ui/GradientText';
import Card3D from '../components/ui/Card3D';

// Chart colors with Gen Z palette
const COLORS = {
  primary: ['#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'],
  secondary: ['#EC4899', '#F472B6', '#F9A8D4', '#FBCFE8'],
  accent: ['#22D3EE', '#A855F7', '#FB923C', '#4ADE80', '#FACC15'],
  dark: ['#1E293B', '#334155', '#475569', '#64748B']};

const AnalyticsPage = () => {
  const { data: apiData, loading, error } = useApi('analytics');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [selectedRole, setSelectedRole] = useState('all'); // 'all' or specific role
  const [timeHireRange, setTimeHireRange] = useState('90days'); // '30days', '90days', '6months', '1year'
  
  // Update analytics data when API data is received
  useEffect(() => {
    if (apiData) {
      setAnalyticsData(apiData);
    }
  }, [apiData, loading]);
  
  const keyMetrics = analyticsData?.keyMetrics || {};
  const sourcePerformance = analyticsData?.sourcePerformance || [];
  const timeToHireByRole = analyticsData?.timeToHireByRole || [];

  // Custom tooltip removed

  return (
    <div className="flex bg-gray-50 dark:bg-dark-900 min-h-screen font-sans">
      <Sidebar />
      <main className="flex-1 p-8">
        <DashboardHeader />
        <div className="mt-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <GradientText variant="primary" as="h1" className="text-2xl font-bold mb-2">
                Analytics & Reports
              </GradientText>
              <p className="text-gray-500 dark:text-gray-400">
                Track your recruitment performance and make data-driven decisions
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-100 dark:border-dark-700 p-1">
                {['week', 'month', 'quarter', 'year'].map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-3 py-1.5 text-sm rounded-md transition-all ${timeRange === range
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                  >
                    {range.charAt(0).toUpperCase() + range.slice(1)}
                  </button>
                ))}
              </div>

              <GradientButton
                variant="primary"
                size="md"
                icon={<FiDownload size={16} />}
                className="shadow-sm"
              >
                Export Data
              </GradientButton>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <MetricCard
              title="Avg. Time to Hire"
              value={`${keyMetrics.avgTimeToHire} days`}
              icon={<FiClock className="text-indigo-500" />}
              change={-2.5}
              trend="down"
              trendGood={true}
            />
            <MetricCard
              title="Total Applicants"
              value={keyMetrics.totalApplicants.toLocaleString()}
              icon={<FiUsers className="text-blue-500" />}
              change={12.7}
              trend="up"
              trendGood={true}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Candidate Source */}
            <Card3D className="p-6 bg-white dark:bg-dark-800" glare={true} maxTilt={5}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white">Candidate Source</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Where your candidates come from</p>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium px-2.5 py-1 rounded-full">
                  This {timeRange}
                </div>
              </div>
              <div className="mt-4">
                <div className="grid grid-cols-2 gap-4">
                  {sourcePerformance.map((source, index) => (
                    <div key={index} className="bg-white/5 backdrop-filter backdrop-blur-sm rounded-lg border border-white/10 p-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: COLORS.accent[index % COLORS.accent.length] }}
                        ></div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-800 dark:text-white">{source.name}</h4>
                          <div className="flex items-center justify-between mt-1">
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                              <div 
                                className="h-2.5 rounded-full" 
                                style={{ 
                                  width: `${(source.value / sourcePerformance.reduce((acc, curr) => acc + curr.value, 0) * 100).toFixed(0)}%`,
                                  backgroundColor: COLORS.accent[index % COLORS.accent.length]
                                }}
                              ></div>
                            </div>
                            <span className="ml-2 text-xs font-medium text-gray-500 dark:text-gray-300">{source.value}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  <p>Total candidates sourced this {timeRange}: {sourcePerformance.reduce((acc, curr) => acc + curr.value, 0)}</p>
                </div>
              </div>
            </Card3D>

            {/* Time to Hire by Role */}
            <GlassCard variant="primary" className="p-6" glow>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">Time to Hire by Role</h3>
                  <p className="text-sm text-indigo-200">Average days to fill positions</p>
                </div>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-wrap gap-3 mb-6">
                {/* Role Selection */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-indigo-200 mb-2">Select Role</label>
                  <div className="relative">
                    <select 
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 text-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      <option value="all">All Roles</option>
                      {timeToHireByRole.map((item, index) => (
                        <option key={index} value={item.role}>{item.role}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-4 h-4 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Time Range Selection */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-indigo-200 mb-2">Time Period</label>
                  <div className="relative">
                    <select 
                      value={timeHireRange}
                      onChange={(e) => setTimeHireRange(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 text-white text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      <option value="30days">Last 30 Days</option>
                      <option value="90days">Last 90 Days</option>
                      <option value="6months">Last 6 Months</option>
                      <option value="1year">Last Year</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                      <svg className="w-4 h-4 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Summary instead of chart */}
              <div className="bg-white/10 backdrop-filter backdrop-blur-sm rounded-xl border border-white/20 p-6 mt-4">
                <div className="space-y-4">
                  {(selectedRole === 'all' ? timeToHireByRole : timeToHireByRole.filter(item => item.role === selectedRole)).map((role, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: `rgba(255,255,255,${0.4 + (index * 0.1)})` }}></div>
                        <span className="text-white font-medium">{role.role}</span>
                      </div>
                      <div className="text-indigo-200 font-bold">{role.days} days</div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-white/10 text-center">
                  <p className="text-indigo-200 text-sm">
                    {selectedRole === 'all' 
                      ? `Average time to hire across all roles: ${Math.round(timeToHireByRole.reduce((acc, role) => acc + role.days, 0) / timeToHireByRole.length)} days` 
                      : `Selected time period: ${timeHireRange === '30days' ? 'Last 30 Days' : timeHireRange === '90days' ? 'Last 90 Days' : timeHireRange === '6months' ? 'Last 6 Months' : 'Last Year'}`}
                  </p>
                </div>
              </div>
            </GlassCard>

          </div>
        </div>
      </main>
    </div>
  );
};

const MetricCard = ({ title, value, icon, change, trend, trendGood }) => {
  // Determine trend color and icon
  const trendColor = trend === 'up' 
    ? (trendGood ? 'text-green-500' : 'text-red-500')
    : (trendGood ? 'text-green-500' : 'text-red-500');
  
  const trendIcon = trend === 'up' 
    ? <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 5L6 2L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6 10V2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    : <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 7L6 10L9 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6 2V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>;
  
  return (
    <Card3D className="bg-white dark:bg-dark-800 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow" maxTilt={5}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
            {change && (
              <div className={`flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
                {trendIcon}
                {Math.abs(change)}%
              </div>
            )}
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{value}</p>
        </div>
        {icon && (
          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
            {icon}
          </div>
        )}
      </div>
    </Card3D>
  );
};

// ChartCard component removed

export default AnalyticsPage;
